import React, { useEffect, useState } from "react";
import { Plus, X, ClipboardList, Send } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Inp, Sl } from "./index";
import { notify } from "../notify";
import { useFormDefinition, fieldsOf, fieldOf, ratingsOf, missingRequired } from "../formCatalog";
import { fmtDMY } from "../dateFormat";
import { usePermissions } from "../usePermissions";

// "15-Day Training Feedback" — two halves on one record: the task and the
// feedback form itself, filled by the reviewing manager or HR. Its layout
// comes from /api/form-templates. See server/routes/trainingReviews.js.

const STATUS_COLOR = { Open: K.bl, "In Progress": K.a, Completed: K.g, Cancelled: K.t3 };
const PRIORITY_COLOR = { Low: K.t3, Normal: K.bl, High: K.a, Urgent: K.r };
const STATUSES = ["Open", "In Progress", "Completed", "Cancelled"];
const PRIORITIES = ["Low", "Normal", "High", "Urgent"];
const OUTCOMES = ["Pending", "Qualified", "Not Qualified"];
// Labels, options, order, required flags and which fields show at all come
// from /api/form-templates — the definition the Form Template tab edits.


const fmtDate = (d) => d ? fmtDMY(d) : '—';

export const EmployeeTrainingReviewTab = ({ employeeId, role, myEmployeeId }) => {
  const { can: pcan } = usePermissions(true);
  const canManage = pcan('employees', 'training_review', null, 'edit');
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get('/api/training-reviews?employee_id=' + employeeId).then((d) => setRows(Array.isArray(d?.rows) ? d.rows : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [employeeId]); // eslint-disable-line

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-bold text-t0 flex items-center gap-2"><ClipboardList size={13} color={K.p} /> Training Review Tasks</div>
        {canManage && <Bt icon={Plus} s="sm" onClick={() => setShowAdd(true)}>Add Review Task</Bt>}
      </div>

      {rows === null ? (
        <div className="text-center p-6 text-t3 text-[14px]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-center p-8 text-t3 text-[14px] bg-bg2 rounded-lg border border-b0">No training review task created yet.</div>
      ) : (
        <div className="grid gap-2">
          {rows.map((r) => (
            <div key={r.id} onClick={() => setOpenId(r.id)} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-bg2 border border-b0 cursor-pointer hover:border-p flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-bold text-t0 truncate">{r.task_name}</div>
                <div className="text-[12px] text-t3 mt-[2px]">Assigned to {r.assigned_to_name || r.assigned_to || '—'} · Due {fmtDate(r.due_date)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Bg c={PRIORITY_COLOR[r.priority] || K.t3} sm>{r.priority}</Bg>
                <Bg c={STATUS_COLOR[r.status] || K.t3} sm dot>{r.status}</Bg>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddReviewModal
          employeeId={employeeId}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {openId && (
        <ReviewDetailModal
          id={openId}
          role={role}
          myEmployeeId={myEmployeeId}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
};

const AddReviewModal = ({ employeeId, onClose, onCreated }) => {
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 15); return d.toISOString().slice(0, 10); });
  const [reminderAt, setReminderAt] = useState('');
  const [priority, setPriority] = useState('High');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/api/training-reviews', {
        employee_id: employeeId, task_name: taskName || null, description: description || null,
        assigned_to: assignedTo || null, start_date: startDate || null, due_date: dueDate || null,
        reminder_at: reminderAt || null, priority,
      });
      notify('Training review task created', { title: 'Saved', variant: 'success' });
      onCreated();
    } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[480px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <span className="text-[16px] font-extrabold text-t0">Add Training Review Task</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>
        <div className="space-y-[10px]">
          <div><div className="text-[14px] text-t2 mb-1">Task Name</div><Inp value={taskName} onChange={setTaskName} placeholder="e.g. Complete 15 Day Training Review- Priyal" /></div>
          <div><div className="text-[14px] text-t2 mb-1">Description</div><textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full min-h-[60px] px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y" /></div>
          <div><div className="text-[14px] text-t2 mb-1">Assigned To (Employee ID)</div><Inp value={assignedTo} onChange={setAssignedTo} placeholder="e.g. E0057" /></div>
          <div className="grid grid-cols-2 gap-[10px]">
            <div><div className="text-[14px] text-t2 mb-1">Start Date</div><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
            <div><div className="text-[14px] text-t2 mb-1">Due Date</div><input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
          </div>
          <div className="grid grid-cols-2 gap-[10px]">
            <div><div className="text-[14px] text-t2 mb-1">Reminder</div><input type="date" value={reminderAt} onChange={(e) => setReminderAt(e.target.value)} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
            <div><div className="text-[14px] text-t2 mb-1">Priority</div><Sl options={PRIORITIES.map((p) => ({ v: p, l: p }))} value={priority} onChange={setPriority} /></div>
          </div>
          <div className="text-[12px] text-t3">The reminder date is recorded on the review — it isn't emailed out automatically.</div>
        </div>
        <div className="flex gap-[6px] mt-4">
          <Bt full disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

const ReviewDetailModal = ({ id, role, myEmployeeId, onClose, onChanged }) => {
  const { can: pcan } = usePermissions(true);
  const canManage = pcan('employees', 'training_review', null, 'edit');
  const [data, setData] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/api/training-reviews/' + id).then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const patch = async (body) => {
    setBusy(true);
    try { await api.put('/api/training-reviews/' + id, body); load(); onChanged?.(); }
    catch (e) { notify(e.message || 'Failed', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post('/api/training-reviews/' + id + '/comments', { comment: comment.trim() }); setComment(''); load(); }
    catch (e) { notify(e.message || 'Failed', { title: 'Error', variant: 'error' }); }
  };

  if (!data) return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onClick={onClose}><Cd style={{ padding: 20 }}>Loading…</Cd></div>;

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="bg-bg1 border border-b1 rounded-2xl w-full max-w-[560px] max-h-[92vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="min-w-0">
            <div className="text-[16px] font-extrabold text-t0 truncate">{data.task_name}</div>
            <div className="text-[14px] text-t3">Initiated by {data.created_by_name || data.created_by || '—'}</div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-4">
            <RO label="Task Owner" value={data.employee_name || data.employee_id} />
            <RO label="Assigned To" value={data.assigned_to_name || data.assigned_to || '—'} />
            <RO label="Start Date" value={fmtDate(data.start_date)} />
            <RO label="Due Date" value={fmtDate(data.due_date)} />
            <RO label="Reminder" value={fmtDate(data.reminder_at)} />
            <div>
              <div className="text-[12px] text-t3 uppercase mb-1">Priority</div>
              {canManage ? <Sl options={PRIORITIES.map((p) => ({ v: p, l: p }))} value={data.priority} onChange={(v) => patch({ priority: v })} /> : <Bg c={PRIORITY_COLOR[data.priority] || K.t3} sm>{data.priority}</Bg>}
            </div>
            <div>
              <div className="text-[12px] text-t3 uppercase mb-1">Status</div>
              <Sl options={STATUSES.map((s) => ({ v: s, l: s }))} value={data.status} onChange={(v) => patch({ status: v })} disabled={busy} />
            </div>
            <div>
              <div className="text-[12px] text-t3 uppercase mb-1">Outcome</div>
              <Sl options={OUTCOMES.map((o) => ({ v: o, l: o }))} value={data.outcome || 'Pending'} onChange={(v) => patch({ outcome: v })} disabled={busy} />
            </div>
          </div>

          {data.description && (
            <div className="p-3 rounded-lg bg-bg2 border border-b0 mb-4">
              <div className="text-[12px] text-t3 uppercase mb-1">Description</div>
              <div className="text-[14px] text-t0 whitespace-pre-wrap">{data.description}</div>
            </div>
          )}

          <FeedbackForm data={data} canFill={canManage || data.assigned_to === myEmployeeId} onSaved={() => { load(); onChanged?.(); }} />

          <div className="text-[14px] font-bold text-t0 mb-2">Comments</div>
          <div className="space-y-2 mb-3">
            {(data.comments || []).length === 0 && <div className="text-[14px] text-t3">No comments yet.</div>}
            {(data.comments || []).map((c) => (
              <div key={c.id} className="p-2 rounded-lg bg-bg2 border border-b0">
                <div className="text-[14px] font-semibold text-t0">{c.employee_name || c.employee_id || 'Someone'}</div>
                <div className="text-[14px] text-t1">{c.comment}</div>
                <div className="text-[12px] text-t3 mt-1">{fmtDMY(c.created_at)}, {new Date(c.created_at).toLocaleTimeString('en-IN')}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-[6px]">
            <Inp value={comment} onChange={setComment} placeholder="Write a comment…" />
            <Bt icon={Send} onClick={addComment} disabled={!comment.trim()} />
          </div>
        </div>
      </div>
    </div>
  );
};


// ─── Performance & Training Evaluation + Manager Remarks ─────────────────
// Saved through its own endpoint (PUT /:id/feedback) so filling the form
// never touches the task half (status, priority, dates) and vice versa.
const FeedbackForm = ({ data, canFill, onSaved }) => {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const { def, loading: defLoading, error: defError } = useFormDefinition('training_feedback');
  const submitted = !!data.feedback_submitted_at;
  const set = (patch) => setVals((prev) => ({ ...prev, ...patch }));
  const v = (k) => (vals[k] !== undefined ? vals[k] : (data[k] ?? ''));
  const sectionTitle = (id, fallback) => (def?.sections || []).find((x) => x.id === id)?.title || fallback;

  const save = async (submit) => {
    if (submit) {
      const merged = { ...data, ...vals };
      const missing = ['employee', 'evaluation', 'remarks']
        .map((sec) => missingRequired(def, sec, merged))
        .find(Boolean);
      if (missing) { notify(`"${missing.label}" is required`, { title: 'Error', variant: 'error' }); return; }
    }
    setBusy(true);
    try {
      await api.put('/api/training-reviews/' + data.id + '/feedback', { ...vals, submit: submit || undefined });
      notify(submit ? 'Training feedback submitted' : 'Saved', { title: submit ? 'Submitted' : 'Saved', variant: 'success' });
      setVals({});
      onSaved?.();
    } catch (e) { notify(e.message || 'Failed to save', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  // One field, drawn as the template says.
  const Field = ({ f }) => {
    const label = <div className="text-[12px] text-t3 uppercase mb-1">{f.label}{f.required && <span style={{ color: K.r }}> *</span>}</div>;
    if (f.type === 'read') {
      const value = f.id === 'join_date' ? fmtDate(data.join_date)
        : f.id === 'employee_name' ? (data.employee_name || data.employee_id)
        : data[f.id];
      return <RO label={f.label} value={value} />;
    }
    if (!canFill) {
      return (
        <div>
          {label}
          {f.type === 'textarea'
            ? <div className="p-2 rounded-lg bg-bg2 border border-b0 text-[14px] text-t0 whitespace-pre-wrap">{data[f.id] || '—'}</div>
            : f.type === 'date' ? <div className="text-[14px] text-t0">{fmtDate(data[f.id])}</div>
            : <Bg c={K.t3} sm>{data[f.id] || '—'}</Bg>}
        </div>
      );
    }
    return (
      <div className={f.full ? 'sm:col-span-2' : ''}>
        {label}
        {f.type === 'textarea' ? (
          <textarea value={v(f.id) || ''} onChange={(e) => set({ [f.id]: e.target.value })} disabled={busy}
            className="w-full min-h-[60px] px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y" />
        ) : f.type === 'date' ? (
          <input type="date" value={(v(f.id) || '').slice(0, 10)} onChange={(e) => set({ [f.id]: e.target.value })} disabled={busy}
            className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" />
        ) : (
          <Sl options={[{ v: '', l: 'Select' }, ...(f.options || []).map((o) => ({ v: o, l: o }))]}
            value={v(f.id) || ''} onChange={(x) => set({ [f.id]: x || null })} disabled={busy} />
        )}
        {f.note && <div className="text-[12px] text-t3 mt-1">{f.note}</div>}
      </div>
    );
  };

  if (defLoading && !def) return <div className="mb-4 text-center py-6 text-t3 text-[14px]">Loading the form…</div>;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[14px] font-bold text-t0">Training Feedback</div>
        {submitted && <Bg c={K.g} sm dot>Submitted {fmtDate(data.feedback_submitted_at)}</Bg>}
      </div>
      {defError && <div className="mb-3 p-2 rounded-lg text-[14px]" style={{ background: K.rb, color: K.r }}>{defError}</div>}

      {['employee', 'evaluation', 'remarks'].map((secId, i) => {
        const fields = fieldsOf(def, secId);
        if (!fields.length) return null;
        return (
          <div key={secId} className="mb-3">
            {i > 0 && <div className="text-[12px] text-t2 font-semibold uppercase tracking-wide mb-2">{sectionTitle(secId, '')}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px]">
              {fields.map((f) => <Field key={f.id} f={f} />)}
            </div>
          </div>
        );
      })}

      {canFill && (
        <div className="flex gap-[6px]">
          <Bt v="secondary" full disabled={busy} onClick={() => save(false)}>Save</Bt>
          <Bt icon={Send} full disabled={busy} onClick={() => save(true)}>{submitted ? 'Re-submit' : 'Submit Feedback'}</Bt>
        </div>
      )}
    </div>
  );
};

const RO = ({ label, value }) => (
  <div className="bg-bg2 border border-b0 rounded p-2">
    <div className="text-[12px] text-t3 uppercase">{label}</div>
    <div className="text-[14px] text-t0 mt-[2px]">{value || '—'}</div>
  </div>
);
